/**
 * YaaS API response transformation helpers
 */

import { USDV_DECIMALS } from "@shared/tokens/usdv";
import type {
    YaasPosition,
    YaasPositionCardData,
    YaasProgramLpPosition,
    YaasStatsResponse,
    YaasWalletResponse,
    YaasWalletStats
} from "@solomon-apps/shared";
import { formatCurrency, formatCurrencyCompact } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";

/**
 * Pool type display mapping
 */
const POOL_TYPE_MAP: Record<string, string> = {
    DEX_LP: "DEX LP",
    TREASURY: "Treasury",
    NEOBANK: "Neobank",
    LENDING: "Lending",
    WALLET: "Wallet"
};

/**
 * Format pool type for display
 * e.g., "DEX_LP" -> "DEX LP"
 */
function formatPoolType(type: string): string {
    return POOL_TYPE_MAP[type] ?? type;
}

/**
 * Map API position status to display status
 * - null -> "Enable" (position not enabled yet)
 * - "Pending" -> "Pending" (awaiting admin approval)
 * - "Disabled" -> "Disabled" (user paused earning; reactivatable)
 * - "Active" -> "Earning" (enabled and active)
 * - anything else -> "Inactive" (admin-side states: Suspended,
 *   Rejected, Hidden, Ended, or statuses this app doesn't know yet).
 *   Only "Active" may render as Earning — a Suspended position must
 *   never tell the user they are still accruing rewards.
 */
function mapPositionStatus(apiStatus: string | null): "Enable" | "Pending" | "Earning" | "Disabled" | "Inactive" {
    if (apiStatus === null) return "Enable";
    if (apiStatus === "Pending") return "Pending";
    if (apiStatus === "Disabled") return "Disabled";
    if (apiStatus === "Active") return "Earning";
    return "Inactive";
}

/** The LP's USDv leg at yaas-api's last persisted observation, as a decimal. */
function lastObservedUsdvAmount(lp: YaasProgramLpPosition | undefined): string | undefined {
    if (!lp || !("amountX" in lp)) return undefined;
    const atomic =
        lp.tokenX === lp.observedAssetMint ? lp.amountX : lp.tokenY === lp.observedAssetMint ? lp.amountY : null;
    return atomic === null ? undefined : new BigDecimal(atomic).divide(BigDecimal.pow10(USDV_DECIMALS)).toString();
}

/**
 * Transform API position to display format
 *
 * @param position - API position object
 * @param apy - Base APY value (from AppContext averageAPY)
 * @param formattedApy - Formatted APY string
 * @param accountFallback - Wallet account's destination + address used when
 *   the position has no per-position rewardsDestinationAddress. Required
 *   because the pool's address is NOT the user's payout destination — that
 *   was a pre-existing bug at line 80.
 * @returns Transformed position for UI display
 */
export function transformPosition(
    position: YaasPosition,
    apy: number = 0,
    formattedApy: string = "-",
    accountFallback: { rewardsDestinationAddress: string | null; address: string }
): YaasPositionCardData {
    const amount = new BigDecimal(position.amount);
    const rewards = new BigDecimal(position.totalRewards);

    // Transform pool name: "USDv-USDC" -> "USDv / USDC"
    const pairDisplay = position.surface.displayName?.replace("-", " / ") ?? "Program Surface";

    const protocolName = position.surface.providerName;

    return {
        id: position.id,
        uuid: position.uuid,
        pool: {
            pair: pairDisplay,
            protocol: protocolName,
            type: formatPoolType(position.surface.displayType ?? "")
        },
        position: {
            lpAddress: position.surface.address ?? "",
            lpIdentity: position.lpPosition,
            lastObservedUsdvAmount: lastObservedUsdvAmount(position.lpPosition),
            usdvAmountExact: position.lpPosition ? null : position.amount,
            USDvValue: position.lpPosition ? 0 : amount.toNumber(),
            formattedUSDvValue: position.lpPosition ? "—" : formatCurrency(position.amount),
            apy,
            formattedApy,
            yaasApy: apy,
            formattedYaasApy: formattedApy,
            status: mapPositionStatus(position.status),
            isActive: rewards.gt(0)
        },
        rewards: {
            destination_address:
                position.rewardsDestinationAddress ??
                accountFallback.rewardsDestinationAddress ??
                accountFallback.address,
            rewards_amount: formatCurrency(position.totalRewards)
        },
        splits: position.splits ?? []
    };
}

/**
 * Transform API wallet response to wallet stats
 *
 * @param walletData - API wallet response
 * @param statsData - Optional global YaaS stats (for TVL)
 * @returns Wallet statistics for UI display
 */
export function transformWalletStats(walletData: YaasWalletResponse, statsData?: YaasStatsResponse): YaasWalletStats {
    const totalPositions = new BigDecimal(walletData.account?.totalPositionsValue ?? "0");
    const totalYieldEarned = new BigDecimal(walletData.account?.totalEarnedRewards ?? "0");
    const totalTvl = new BigDecimal(statsData?.computedTotalTvl ?? "0");

    return {
        totalPositions: totalPositions.toNumber(),
        totalPositionsExact: totalPositions,
        formattedTotalPositions: formatCurrency(walletData.account?.totalPositionsValue ?? "0"),
        totalYieldEarned: totalYieldEarned.toNumber(),
        totalYieldEarnedExact: totalYieldEarned,
        formattedTotalYieldEarned: formatCurrency(walletData.account?.totalEarnedRewards ?? "0"),
        totalTvl: totalTvl.toNumber(),
        totalTvlExact: totalTvl,
        formattedTvl: statsData ? formatCurrencyCompact(statsData.computedTotalTvl, 2) : "$0"
    };
}

/**
 * Get the earliest position creation date from a list of positions
 *
 * @param positions - Array of positions
 * @returns ISO date string of earliest position, or null if no positions
 */
export function getEarliestPositionDate(positions: YaasPosition[]): string | null {
    if (!positions || positions.length === 0) return null;

    return positions.reduce((earliest: string, pos: YaasPosition) => {
        return pos.createdAt < earliest ? pos.createdAt : earliest;
    }, positions[0].createdAt);
}

/** Both balance providers use this projection. Only presentation converts to number. */
export function withPositionAmount(
    position: YaasPositionCardData,
    amount: string | BigDecimal | null
): YaasPositionCardData {
    const exact = amount === null ? null : new BigDecimal(amount);
    return {
        ...position,
        position: {
            ...position.position,
            usdvAmountExact: exact?.toString() ?? null,
            USDvValue: exact?.toNumber() ?? 0,
            formattedUSDvValue: exact === null ? "—" : formatCurrency(exact.toString())
        }
    };
}
